import { useEffect, useState } from 'react';

import useGroupRoleQuery from 'lane-shared/hooks/useGroupRoleQuery';

type GetDisplayChannelIdProps = {
  groupRoleId?: string | null;
  resolvedGroupRoleChannelId?: string | null;
  selectedChannelId?: string;
  channelId?: string;
  fallbackChannelId?: string;
  enablePersistedGroupRoleChannelId?: boolean;
};

function getDisplayChannelId({
  groupRoleId,
  resolvedGroupRoleChannelId,
  selectedChannelId,
  channelId,
  fallbackChannelId,
  enablePersistedGroupRoleChannelId,
}: GetDisplayChannelIdProps) {
  if (groupRoleId) {
    if (enablePersistedGroupRoleChannelId) {
      // Persisted rule channel hydration is opt-in and only enabled where
      // flagged (Payment advanced rules) to avoid changing legacy callers.
      return resolvedGroupRoleChannelId || selectedChannelId || channelId;
    }

    return resolvedGroupRoleChannelId || selectedChannelId;
  }

  return selectedChannelId || fallbackChannelId;
}

type UseGroupRoleSelectorChannelStateProps = {
  groupRoleId?: string | null;
  groupRoleChannelId?: string | null;
  channelId?: string;
  fallbackChannelId?: string;
  enablePersistedGroupRoleChannelId?: boolean;
};

export const useGroupRoleSelectorChannelState = ({
  groupRoleId,
  groupRoleChannelId,
  channelId,
  fallbackChannelId,
  enablePersistedGroupRoleChannelId = false,
}: UseGroupRoleSelectorChannelStateProps) => {
  const shouldHydrateFromPersistedChannel =
    enablePersistedGroupRoleChannelId && Boolean(groupRoleId);
  const [selectedChannelId, setSelectedChannelId] = useState<
    string | undefined
  >(shouldHydrateFromPersistedChannel ? groupRoleChannelId || undefined : channelId);

  useEffect(() => {
    // If the channel prop changes, sync it only while no custom channel is selected.
    // selectedChannelId is intentionally omitted from deps — matching the original
    // [channelId]-only dependency of GroupRoleSelectorV2. Including it would cause
    // the effect to re-fire when selectedChannelId is cleared (e.g. after selecting
    // a group role with no channel), immediately resetting it back to channelId.
    if (channelId && !selectedChannelId) {
      setSelectedChannelId(channelId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId]);

  const { groupRole } = useGroupRoleQuery({ groupRoleId });
  const resolvedGroupRoleChannelId =
    groupRole?.channel?._id ||
    (shouldHydrateFromPersistedChannel ? groupRoleChannelId : null);

  useEffect(() => {
    if (shouldHydrateFromPersistedChannel && resolvedGroupRoleChannelId) {
      setSelectedChannelId(resolvedGroupRoleChannelId);
    }
  }, [resolvedGroupRoleChannelId, shouldHydrateFromPersistedChannel]);

  const displayChannelId = getDisplayChannelId({
    groupRoleId,
    resolvedGroupRoleChannelId,
    selectedChannelId,
    channelId,
    fallbackChannelId,
    enablePersistedGroupRoleChannelId: shouldHydrateFromPersistedChannel,
  });

  return {
    groupRole,
    displayChannelId,
    setSelectedChannelId,
  };
};
